/**
 * Occurrence-window math for the site scheduling calendar.
 *
 * The calendar fetches server-expanded occurrences for a date window rather than
 * per view. The window is the full month grid around the visible date padded by
 * a week on each side, so day/week/agenda views (all narrower and contained
 * within that span) are covered without a refetch, and navigating within a month
 * reuses the fetched data. A refetch is only needed when the visible date moves
 * to a month whose padded grid the last fetch does not cover.
 *
 * Contains:
 * - computeOccurrenceWindow — visible date -> {start, end} date strings
 * - isWindowCovered — does a fetched window already cover a needed one
 *
 * Returns plain JS — no JSX, no redux.
 */
import { addWeeks, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek, subWeeks } from 'date-fns';

const DATE_ONLY = 'yyyy-MM-dd';
const GRID_PADDING_WEEKS = 1;

/**
 * @param {Date} visibleDate - The scheduler's visible date.
 * @param {number} [weekStartsOn=0] - date-fns week start (0 = Sunday).
 * @returns {{start: string, end: string}} Inclusive `yyyy-MM-dd` window bounds.
 */
export function computeOccurrenceWindow(visibleDate, weekStartsOn = 0) {
  const gridStart = startOfWeek(startOfMonth(visibleDate), { weekStartsOn });
  const gridEnd = endOfWeek(endOfMonth(visibleDate), { weekStartsOn });
  return {
    start: format(subWeeks(gridStart, GRID_PADDING_WEEKS), DATE_ONLY),
    end: format(addWeeks(gridEnd, GRID_PADDING_WEEKS), DATE_ONLY),
  };
}

/**
 * @param {{start: ?string, end: ?string}} fetched - The last fetched window.
 * @param {{start: string, end: string}} needed - The window now required.
 * @returns {boolean} True when the fetched window fully spans the needed one.
 */
export function isWindowCovered(fetched, needed) {
  if (!fetched || !fetched.start || !fetched.end) {
    return false;
  }
  return fetched.start <= needed.start && fetched.end >= needed.end;
}
